/**
 * Display formatter for Control Centre KPI modal timestamps, in UK time.
 * "2026-07-30T13:10:28.017Z" → "2026-07-30 14:10:28" (BST)
 *
 * Zone-less Trino-style values ("2026-07-30 13:10:28") are UTC by backend
 * convention. Pass `timeBasis: 'supplied'` for Aquarius-origin columns, whose
 * stored clock digits are the supplier's own wall clock (see `formatSuppliedClock`).
 */
import { formatSuppliedClock, formatUkTableDateTime } from './ukTime';

const ISO_LIKE =
  /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:?\d{2}(?::\d{2})?)?$/;

export type TimeBasis = 'instant' | 'supplied';

/** Format an ISO / Trino-style datetime for fleet-manager tables. Leaves other values unchanged. */
export function formatControlCentreDateTime(
  value: unknown,
  timeBasis: TimeBasis = 'instant',
): string {
  if (value == null || value === '') return '';
  // Only treat numeric values as epochs when they look like ms timestamps (avoid reformatting scores/counts)
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value < 1e11) return String(value); // not a ms epoch
    return formatUkTableDateTime(value, String(value));
  }
  const raw = String(value).trim();
  if (!ISO_LIKE.test(raw)) {
    return raw;
  }
  return timeBasis === 'supplied'
    ? formatSuppliedClock(raw, {}, raw)
    : formatUkTableDateTime(raw, raw);
}
